import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './i18n';
import { trackPageView, trackEvent, initGlobalClickTracking, initEngagementTracking } from './lib/analytics';
import NotFoundPage from './pages/NotFoundPage';
import SEO from './components/SEO';
import Header from './components/Header';
import Hero from './components/Hero';
import Trusted from './components/Trusted';
import AIMonitoringStrip from './components/AIMonitoringStrip';
import AIActivityTimeline from './components/AIActivityTimeline';
import GrowthEngines from './components/GrowthEngines';
import Challenges from './components/Challenges';
import Comparison from './components/Comparison';
import { PublicEffectDemo } from './components/PublicEffectDemo';
import OperatingIntelligence from './components/OperatingIntelligence';
import Insights from './components/Insights';
import { AffiliateStrip } from './components/AffiliateStrip';
import MidCta from './components/MidCta';
import UserWayWidget from './components/UserWayWidget';
import AuditForm from './components/AuditForm';
import FreeAuditPage from './pages/FreeAuditPage';
import ScorePage from './pages/ScorePage';
import InternalLinkFooter from './components/InternalLinkFooter';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import AIConcierge from './components/AIConcierge';
import InsightPopup from './components/InsightPopup';
import ExitIntentBanner from './components/ExitIntentBanner';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import ServicesPage from './pages/ServicesPage';
import ItPosPage from './pages/ItPosPage';
import RevenueGrowthPage from './pages/RevenueGrowthPage';
import DigitalBrandingPage from './pages/DigitalBrandingPage';
import AiAutomationPage from './pages/AiAutomationPage';
import AiAgentsPage from './pages/AiAgentsPage';
import IndexAiPage from './pages/IndexAiPage';
import GoPage from './pages/GoPage';
import OnlineOrderingPage from './pages/OnlineOrderingPage';
import RestaurantWebsitesPage from './pages/RestaurantWebsitesPage';
import AboutUsPage from './pages/AboutUsPage';
import FaqPage from './pages/FaqPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AccessibilityPage from './pages/AccessibilityPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import HowItWorksPage from './pages/HowItWorksPage';
import OperatingIntelligencePage from './pages/OperatingIntelligencePage';
import CateringAiPage from './pages/CateringAiPage';
import MagazinePage from './pages/MagazinePage';
import SharedFolderPage from './pages/SharedFolderPage';
import MakondoWelcomePage from './pages/MakondoWelcomePage';
import ArticlePage from './pages/ArticlePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MagazineCategoryPage from './pages/MagazineCategoryPage';
import MagazineAuthorPage from './pages/MagazineAuthorPage';
import ServiceLandingTemplate from './components/ServiceLandingTemplate';
import DataAnalyticsPage from './pages/DataAnalyticsPage';
import DashboardsPage from './pages/DashboardsPage';
import OsLivePage from './pages/OsLivePage';
import SalesAlert from './components/SalesAlert';
import GoogleBusinessProfilePage from './pages/GoogleBusinessProfilePage';
import CategoryPage from './pages/CategoryPage';
import Breadcrumbs from './components/Breadcrumbs';
import AdminEditorPage from './pages/AdminEditorPage';
import AiTeamPage from './pages/AiTeamPage';
import IntelligencePage from './pages/IntelligencePage';
import AiOsLayout from './pages/os2/AiOsLayout';
import OverviewPage from './pages/os2/OverviewPage';
import SalesPage from './pages/os2/SalesPage';
import CustomersPage from './pages/os2/CustomersPage';
import {
  SettingsOsPage,
} from './pages/os2/ModulePages';
import PipelinePage from './pages/os2/PipelinePage';
import GrowthCenterPage from './pages/os2/GrowthCenterPage';
import SocialIntelligencePage from './pages/os2/SocialIntelligencePage';
import AutonomyPage from './pages/os2/AutonomyPage';
import ReportsOsPage from './pages/os2/ReportsOsPage';
import NotificationsOsPage from './pages/os2/NotificationsOsPage';
import GlossaryPage from './pages/os2/GlossaryPage';
import ChangelogPage from './pages/os2/ChangelogPage';
import SystemHealthPage from './pages/os2/SystemHealthPage';
import RestaurantsPage from './pages/os2/RestaurantsPage';
import RestLayout from './pages/rest/RestLayout';
import RestHome from './pages/rest/RestHome';
import RestLogin from './pages/rest/RestLogin';
import RestDemoEntry from './pages/rest/RestDemoEntry';
import DemoAccessPage from './pages/DemoAccessPage';
import WelcomeGate from './components/WelcomeGate';
import OsHubPage from './pages/OsHubPage';
import DemoEntry from './pages/DemoEntry';
import RestMissionControl from './pages/rest/RestMissionControl';
import RestRevenue from './pages/rest/RestRevenue';
import RestLocations from './pages/rest/RestLocations';
import RestDelivery from './pages/rest/RestDelivery';
import RestReports from './pages/rest/RestReports';
import RestConnections from './pages/rest/RestConnections';
import RestIntelligence from './pages/rest/RestIntelligence';
import WeeklyReportPage from './pages/WeeklyReportPage';
import AdminUsersPage from './pages/os2/UsersPage';
import RestBrief from './pages/rest/RestBrief';
import RestSettings from './pages/rest/RestSettings';
import { RestModuleMenu, RestModuleMarketing, RestModuleReviews } from './pages/rest/RestModules';
import RestGuests from './pages/rest/RestGuests';
import MarketingOsPage from './pages/os2/MarketingOsPage';
import AffiliatesOsPage from './pages/os2/AffiliatesOsPage';
import PartnersPanelPage from './pages/os2/PartnersPanelPage';
import ContentOsPage from './pages/os2/ContentOsPage';
import FinanceOsPage from './pages/os2/FinanceOsPage';
import LoginPage from './pages/LoginPage';
import AccountPage from './pages/AccountPage';
import DemoPage from './pages/DemoPage';
import AffiliatesPage from './pages/AffiliatesPage';
import AffiliateDashboardPage from './pages/AffiliateDashboardPage';
import AdminAffiliatesPage from './pages/AdminAffiliatesPage';
import AdminImportLeadsPage from './pages/AdminImportLeadsPage';
import { captureRef } from './lib/referral';
import AdminGate from './components/AdminGate';
import AutoScrollReader from './components/AutoScrollReader';

function ScrollManager() {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    // If a hash is present, poll for the target for up to ~1.6s (async content
    // like the AuditForm image / Contact hydration can delay mount).
    if (hash) {
      const targetId = hash.replace(/^#/, '');
      const start = Date.now();
      let raf;
      const tryScroll = () => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Highlight the section briefly to signal arrival.
          el.classList.add('kx-hash-target');
          setTimeout(() => el.classList.remove('kx-hash-target'), 1400);
          // Auto-focus the first tabbable field for immediate typing.
          const firstInput = el.querySelector(
            'input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
          );
          if (firstInput) {
            setTimeout(() => {
              try { firstInput.focus({ preventScroll: true }); } catch (e) { /* noop */ }
            }, 650);
          }
          return;
        }
        if (Date.now() - start < 1600) {
          raf = requestAnimationFrame(tryScroll);
        } else {
          // Target never mounted — fall back to top so we never leave the user
          // stranded mid-page after a failed anchor navigation.
          window.scrollTo({ top: 0, behavior: 'auto' });
        }
      };
      raf = requestAnimationFrame(tryScroll);
      return () => cancelAnimationFrame(raf);
    }
    window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [pathname, hash, key]);
  return null;
}

function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => { initGlobalClickTracking(); initEngagementTracking(); }, []);
  useEffect(() => {
    trackPageView(pathname);
    if (pathname === '/pricing') trackEvent('pricing_page_view', {});
  }, [pathname]);
  return null;
}

function Home() {
  return (
    <>
      <SEO path="/" />
      <WelcomeGate />
      <Hero />
      <AIMonitoringStrip />
      <Trusted />
      <GrowthEngines />
      <Challenges />
      <MidCta />
      <Comparison />
      <PublicEffectDemo />
      <OperatingIntelligence />
      <AIActivityTimeline compact />
      <Insights />
      <AffiliateStrip className="mt-16" />
      <InternalLinkFooter />
      <AuditForm />
    </>
  );
}

function Layout({ children }) {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin/ai-os') || pathname.startsWith('/restaurant/') || pathname === '/restaurant' || pathname.startsWith('/hub')) {
    return <>{children}</>;
  }
  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <a href="#main" className="kx-skip-link">Skip to content</a>
      <UserWayWidget />
      <Header />
      <main id="main" className="relative kx-zoomable">
        <Breadcrumbs />
        {children}
      </main>
      <Footer />
      <CookieConsent />
      <AIConcierge />
      <SalesAlert />
      <InsightPopup />
      <ExitIntentBanner />
      <Toaster />
      <SonnerToaster position="bottom-center" richColors />
    </div>
  );
}

function App() {
  useEffect(() => { captureRef(); }, []);
  return (
    <div className="App">
      <HelmetProvider>
        <LanguageProvider>
          <BrowserRouter>
            <ScrollManager />
            <AnalyticsTracker />
            <AutoScrollReader />
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/go" element={<GoPage />} />
                <Route path="/services" element={<ServicesPage />} />
                {/* Category-level "explained simply" landing pages */}
                <Route path="/restaurant-technology" element={<CategoryPage slug="restaurant-technology" />} />
                <Route path="/digital-presence" element={<CategoryPage slug="digital-presence" />} />
                <Route path="/restaurant-growth" element={<CategoryPage slug="restaurant-growth" />} />
                <Route path="/restaurant-intelligence" element={<CategoryPage slug="restaurant-intelligence" />} />
                <Route path="/ai-solutions" element={<CategoryPage slug="ai-solutions" />} />
                <Route path="/it-pos-systems" element={<ItPosPage />} />
                <Route path="/restaurant-revenue-growth" element={<RevenueGrowthPage />} />
                <Route path="/digital-branding" element={<DigitalBrandingPage />} />
                <Route path="/ai-and-automation" element={<AiAutomationPage />} />
                <Route path="/ai-agents-for-restaurants" element={<AiAgentsPage />} />
                <Route path="/index-ai" element={<IndexAiPage />} />
                <Route path="/aboutus" element={<AboutUsPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/free-audit" element={<FreeAuditPage />} />
                <Route path="/score" element={<ScorePage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/accessibility" element={<AccessibilityPage />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/cookies" element={<CookiePolicyPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/operating-intelligence" element={<OperatingIntelligencePage />} />
                <Route path="/team" element={<OperatingIntelligencePage />} />
                <Route path="/ai/catering-ai" element={<CateringAiPage />} />
                {/* Magazine — editorial publication */}
                <Route path="/magazine" element={<MagazinePage />} />
                <Route path="/magazine/coleccion/:token" element={<SharedFolderPage />} />
                <Route path="/makondo" element={<MakondoWelcomePage />} />
                <Route path="/magazine/category/:slug" element={<MagazineCategoryPage />} />
                <Route path="/magazine/author/:slug" element={<MagazineAuthorPage />} />
                <Route path="/authors/:slug" element={<MagazineAuthorPage />} />
                <Route path="/ai-team" element={<AiTeamPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/magazine/:slug" element={<ArticlePage />} />
                {/* Internal admin — editorial */}
                <Route path="/admin/editor" element={<AdminGate><AdminEditorPage /></AdminGate>} />
                {/* Phase B+C: reusable landing template for all service & AI product pages. */}
                <Route path="/services/online-ordering" element={<OnlineOrderingPage />} />
                <Route path="/services/restaurant-websites" element={<RestaurantWebsitesPage />} />
                <Route path="/services/google-business-profile" element={<GoogleBusinessProfilePage />} />
                <Route path="/services/data-analytics" element={<DataAnalyticsPage />} />
                <Route path="/services/dashboards" element={<DashboardsPage />} />
                <Route path="/os" element={<OsLivePage />} />
                <Route path="/intelligence" element={<IntelligencePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/r/:token" element={<WeeklyReportPage />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/demo/book" element={<DemoPage />} />
                <Route path="/affiliates" element={<AffiliatesPage />} />
                <Route path="/partners" element={<AffiliatesPage />} />
                <Route path="/affiliates/dashboard" element={<AffiliateDashboardPage />} />
                <Route path="/admin/affiliates" element={<AdminGate><AdminAffiliatesPage /></AdminGate>} />
                <Route path="/admin/import-leads" element={<AdminGate><AdminImportLeadsPage /></AdminGate>} />
                <Route path="/admin/mission-control" element={<Navigate to="/admin/ai-os" replace />} />
                <Route path="/admin/ai-os" element={<AdminGate><AiOsLayout /></AdminGate>}>
                  <Route index element={<OverviewPage />} />
                  <Route path="pipeline" element={<PipelinePage />} />
                  <Route path="growth" element={<GrowthCenterPage />} />
                  <Route path="social" element={<SocialIntelligencePage />} />
                  <Route path="autonomy" element={<AutonomyPage />} />
                  <Route path="workspace" element={<Navigate to="/admin/ai-os/autonomy?tab=team" replace />} />
                  <Route path="sales" element={<Navigate to="/admin/ai-os/pipeline?tab=sales" replace />} />
                  <Route path="marketing" element={<Navigate to="/admin/ai-os/pipeline?tab=marketing" replace />} />
                  <Route path="customers" element={<Navigate to="/admin/ai-os/pipeline?tab=customers" replace />} />
                  <Route path="ai-team" element={<Navigate to="/admin/ai-os/autonomy?tab=team" replace />} />
                  <Route path="affiliates" element={<AffiliatesOsPage />} />
                  <Route path="partners" element={<PartnersPanelPage />} />
                  <Route path="content" element={<ContentOsPage />} />
                  <Route path="finance" element={<FinanceOsPage />} />
                  <Route path="operations" element={<Navigate to="../system-health" replace />} />
                  <Route path="reports" element={<ReportsOsPage />} />
                  <Route path="notifications" element={<NotificationsOsPage />} />
                  <Route path="glossary" element={<GlossaryPage />} />
                  <Route path="changelog" element={<ChangelogPage />} />
                  <Route path="system-health" element={<SystemHealthPage />} />
                  <Route path="restaurants" element={<RestaurantsPage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="settings" element={<SettingsOsPage />} />
                </Route>
                {/* Restaurant AI OS — private customer instances */}
                <Route path="/restaurant/login" element={<Navigate to="/login" replace />} />
                <Route path="/restaurant/demo" element={<RestDemoEntry />} />
                <Route path="/demo" element={<DemoAccessPage />} />
                <Route path="/demo/verify" element={<DemoAccessPage />} />
                <Route path="/hub" element={<OsHubPage />} />
                <Route path="/demo/os" element={<DemoEntry />} />
                <Route path="/restaurant/login/:slug" element={<Navigate to="/login" replace />} />
                <Route path="/restaurant/invite/:token" element={<RestLogin />} />
                <Route path="/restaurant" element={<RestLayout />}>
                  <Route index element={<RestHome />} />
                  <Route path="intelligence" element={<RestIntelligence />} />
                  <Route path="mission-control" element={<RestMissionControl />} />
                  <Route path="revenue" element={<RestRevenue />} />
                  <Route path="locations" element={<RestLocations />} />
                  <Route path="delivery" element={<RestDelivery />} />
                  <Route path="menu" element={<RestModuleMenu />} />
                  <Route path="guests" element={<RestGuests />} />
                  <Route path="marketing" element={<RestModuleMarketing />} />
                  <Route path="reviews" element={<RestModuleReviews />} />
                  <Route path="ai-executive" element={<RestBrief />} />
                  <Route path="reports" element={<RestReports />} />
                  <Route path="connections" element={<RestConnections />} />
                  <Route path="settings" element={<RestSettings />} />
                </Route>
                <Route path="/services/:slug" element={<ServiceLandingTemplate prefix="services" />} />
                <Route path="/ai/:slug" element={<ServiceLandingTemplate prefix="ai" />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Layout>
          </BrowserRouter>
        </LanguageProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
